import { QUERY, type QueryOperation, useSuspenseQueryOperations } from 'api/Query';
import { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import type { ReactNode } from 'react';
import { useHashAndLocalStorageState } from 'ts/base/hooks/UseHashAndLocalStorageState';
import { DiffModeForm } from 'ts/commons/code/compare/DiffModeForm';
import { COMPARE_MODE_PARAMETER } from 'ts/commons/links/CompareLinks';
import type { DiffDescription } from 'typedefs/DiffDescription';
import type { ExtendedRepositoryLogEntry } from 'typedefs/ExtendedRepositoryLogEntry';
import type { ExtendedTrackedFinding } from 'typedefs/ExtendedTrackedFinding';
import type { FormattedTokenElementInfo } from 'typedefs/FormattedTokenElementInfo';
import type { LineCoverageInfo } from 'typedefs/LineCoverageInfo';
import type { RepositoryLogEntry } from 'typedefs/RepositoryLogEntry';
import { type CompareContentData, type CompareContentInfo } from './CompareContentSide';
import { CompareLayout } from './CompareLayout';

/** The available compare modes in the compare perspective. */
export enum ECompareMode {
	LINE_BASED = 0,
	LINE_BASED_IGNORE_WHITESPACE = 1,
	TOKEN_BASED = 2
}

type CompareCodeComponentProps = {
	leftCompareInfo: CompareContentInfo;
	rightCompareInfo: CompareContentInfo;
	isInconsistentClone?: boolean;
	/** Artificial diffs which should be shown instead of actual diffs */
	artificialDiffs?: DiffDescription;
	maxCodeHeight?: string;
	coverageSelector?: ReactNode;
	showDiffModeChooser?: boolean;
	/**
	 * Whether the given commit should be resolved to point to the last commit where the given file has actually been
	 * changed. You might want to set this to false, when you have a concrete test commit at hand that should be used
	 * for the compare view, so that also coverage is shown for that point in time (TS-40284).
	 */
	resolveCommitToLastChange?: boolean;
	/** Scrolls the compare layout into view once it is rendered. This is used in the finding detail sibling tab. */
	scrollIntoViewOnMount?: boolean;
	showCoverage?: boolean;
	lineCoveragePartitions?: string[] | null;
};

/**
 * Displays a comparison between two files or methods. Delegates the actual rendering logic to {@link CompareLayout},
 * which has no dependency on server calls.
 */
export function CompareCodeComponent({
	leftCompareInfo,
	rightCompareInfo,
	isInconsistentClone = false,
	artificialDiffs,
	maxCodeHeight,
	showDiffModeChooser,
	coverageSelector,
	resolveCommitToLastChange,
	scrollIntoViewOnMount,
	showCoverage,
	lineCoveragePartitions
}: CompareCodeComponentProps) {
	const [{ leftData, rightData, diffs, inconsistentCloneDiffs }] = useSuspenseQueryOperations({
		leftData: buildCompareContentDataOperations(leftCompareInfo, lineCoveragePartitions),
		rightData: buildCompareContentDataOperations(
			rightCompareInfo,
			lineCoveragePartitions,
			resolveCommitToLastChange
		),
		diffs: getDiffOperation(artificialDiffs, leftCompareInfo, rightCompareInfo),
		inconsistentCloneDiffs: getInconsistentCloneDiffOperations(
			isInconsistentClone,
			leftCompareInfo,
			rightCompareInfo
		)
	});
	const defaultDiffMode = ECompareMode.LINE_BASED;
	const [diffMode, setDiffMode] = useHashAndLocalStorageState(COMPARE_MODE_PARAMETER, String(defaultDiffMode));

	let diff = diffs[Number(diffMode)];

	// For some use cases such as inconsistent clone diffs or clone comparisons, we do only support line based diff
	if (diff == null) {
		diff = diffs[defaultDiffMode]!;
	}

	return (
		<div className="flex-1 min-h-0 flex flex-col items-stretch">
			{showDiffModeChooser ? (
				<DiffModeForm
					diffs={diffs}
					diffIndex={Number(diffMode)}
					onDiffModeChanged={mode => setDiffMode(String(mode))}
					coverageSelector={coverageSelector}
				/>
			) : null}
			<CompareLayout
				leftContent={leftCompareInfo}
				rightContent={rightCompareInfo}
				maxCodeHeight={maxCodeHeight}
				leftData={toCompareContentData(leftData)}
				rightData={toCompareContentData(rightData)}
				diff={diff}
				showCoverage={showCoverage}
				scrollIntoViewOnMount={scrollIntoViewOnMount}
				changeRegionsPreviousCommit={
					isInconsistentClone ? computeInconsistentCloneChangeRegions(inconsistentCloneDiffs!) : undefined
				}
				isInconsistentClone={isInconsistentClone}
			/>
		</div>
	);
}

/**
 * The content that is subject to comparison (either left or right). This consists of the element content and also any
 * associated data such as findings.
 */
function buildCompareContentDataOperations(
	compareContentInfo: CompareContentInfo,
	partitions: string[] | null | undefined,
	resolveCommitToLastChange = true
) {
	// Comparison with local file.
	// The constant corresponds to ElementComparisonService.LOCAL_DISK_ACCESS_MARKER.
	if (compareContentInfo.project === '_local_') {
		return {
			content: QUERY.getExternalFileWithPreprocessing(compareContentInfo.uniformPath),
			findings: [],
			logEntry: null
		};
	}

	let lastChangeLogEntry: QueryOperation<RepositoryLogEntry | null> | undefined;
	let logEntry: QueryOperation<ExtendedRepositoryLogEntry[]> | undefined;
	if (resolveCommitToLastChange) {
		lastChangeLogEntry = QUERY.getLastChangeLogEntry(compareContentInfo.project, compareContentInfo.uniformPath, {
			t: compareContentInfo.commit ?? undefined,
			'exclude-non-code-commits': true
		});
	} else {
		logEntry = QUERY.getRepositoryLog(compareContentInfo.project, {
			commit: [compareContentInfo.commit!]
		});
	}
	return {
		content: QUERY.getFormattedTokenElementInfo(compareContentInfo.project, {
			'uniform-path': compareContentInfo.uniformPath,
			t: compareContentInfo.commit ?? undefined
		}),
		findings: QUERY.getFindings(compareContentInfo.project, {
			'uniform-path': compareContentInfo.uniformPath,
			'only-spec-item-findings': false,
			t: compareContentInfo.commit ?? undefined
		}),
		lastChangeLogEntry,
		logEntry,
		lineCoverageInfo: compareContentInfo.skipCoverageCalculation
			? null
			: QUERY.getTestCoverage(compareContentInfo.project, compareContentInfo.uniformPath, {
					t: compareContentInfo.commit ?? undefined,
					'merge-request-id': compareContentInfo.mergeRequestId,
					partitions: partitions ?? undefined,
					'all-partitions': partitions == null
				})
	};
}

function toCompareContentData({
	content,
	findings,
	lastChangeLogEntry,
	logEntry,
	lineCoverageInfo
}: {
	content: FormattedTokenElementInfo | null;
	findings: ExtendedTrackedFinding[];
	lastChangeLogEntry?: RepositoryLogEntry | null;
	logEntry?: ExtendedRepositoryLogEntry[] | null;
	lineCoverageInfo?: LineCoverageInfo | null;
}): CompareContentData {
	return {
		content,
		findings,
		lineCoverageInfo,
		// Only one of both is set depending on resolveCommitToLastChange
		logEntry: logEntry?.[0]?.logEntry ?? lastChangeLogEntry ?? null
	};
}

function getDiffOperation(
	artificialDiffs: DiffDescription | undefined,
	leftCompareInfo: CompareContentInfo,
	rightCompareInfo: CompareContentInfo
) {
	if (artificialDiffs !== undefined) {
		return [artificialDiffs];
	} else {
		const path1 = createExtendedCommitPath(leftCompareInfo, leftCompareInfo.commit);
		const path2 = createExtendedCommitPath(rightCompareInfo, rightCompareInfo.commit);
		let region;
		if (leftCompareInfo.region && rightCompareInfo.region) {
			region = `${leftCompareInfo.region.startLine}-${leftCompareInfo.region.endLine}:${rightCompareInfo.region.startLine}-${rightCompareInfo.region.endLine}`;
		}
		return QUERY.getDiffs({
			left: path1,
			right: path2,
			region
		});
	}
}

function getInconsistentCloneDiffOperations(
	isInconsistentClone: boolean,
	leftCompareInfo: CompareContentInfo,
	rightCompareInfo: CompareContentInfo
) {
	if (!isInconsistentClone) {
		return undefined;
	}
	const path1 = createExtendedCommitPath(leftCompareInfo, leftCompareInfo.commit);
	const path2 = createExtendedCommitPath(rightCompareInfo, rightCompareInfo.commit);
	if (path1 !== path2) {
		return {
			diffPrevCommit: getDiffPrevCommit(rightCompareInfo),
			diffFiles: QUERY.getDiffs({
				left: path1,
				right: path2
			})
		};
	}
	return { diffPrevCommit: getDiffPrevCommit(rightCompareInfo) };
}

function computeInconsistentCloneChangeRegions({
	diffPrevCommit,
	diffFiles
}: {
	diffPrevCommit: DiffDescription[];
	diffFiles?: DiffDescription[];
}) {
	const diffWithWhitespaceIndex = 0;
	let finalDiffs = diffPrevCommit[diffWithWhitespaceIndex]!.leftChangeRegions;

	if (diffFiles !== undefined) {
		const inconsistentCloneDiff = diffFiles[diffWithWhitespaceIndex]!;
		finalDiffs = intersectChangeRegions(finalDiffs, inconsistentCloneDiff.rightChangeRegions);
	}
	return finalDiffs;
}

/** Computes the diff between the current commit and the previous one */
function getDiffPrevCommit(compareContentInfo: CompareContentInfo): QueryOperation<DiffDescription[]> {
	const currentCommitPath = createExtendedCommitPath(compareContentInfo, compareContentInfo.commit);
	const previousCommitPath = createExtendedCommitPath(
		compareContentInfo,
		UnresolvedCommitDescriptor.getPreviousCommit(compareContentInfo.commit)
	);
	return QUERY.getDiffs({
		left: currentCommitPath,
		right: previousCommitPath
	});
}

/** Creates the extended commit path for a specific commit */
function createExtendedCommitPath(
	compareContent: CompareContentInfo,
	commit: UnresolvedCommitDescriptor | null
): string {
	return compareContent.project + '/' + compareContent.uniformPath + '#@#' + (commit ?? '');
}

/**
 * Intersects two change regions. If two change regions intersect each other, the one from the first list will be added
 * to array which is returned.
 */
function intersectChangeRegions(firstChangeRegions: number[], secondChangeRegions: number[]): number[] {
	const changes = [];
	for (let i = 0; i < firstChangeRegions.length; i += 2) {
		for (let j = 0; j < secondChangeRegions.length; j += 2) {
			const startFirst = firstChangeRegions[i]!;
			const endFirst = firstChangeRegions[i + 1]!;
			const startSecond = secondChangeRegions[j]!;
			const endSecond = secondChangeRegions[j + 1]!;

			const firstContainsSecond = startFirst <= startSecond && endFirst >= endSecond;
			const secondContainsFirst = startFirst >= startSecond && endFirst <= endSecond;
			const secondStartInFirstRange = startFirst <= startSecond && endFirst >= startSecond;
			const secondEndInFirstRange = startFirst <= endSecond && endFirst >= endSecond;

			const containsOtherRange = firstContainsSecond || secondContainsFirst;
			const overlap = secondStartInFirstRange || secondEndInFirstRange;

			if (containsOtherRange || overlap) {
				changes.push(startFirst, endFirst);
				break;
			}
		}
	}
	return changes;
}
